import React, {useEffect, useState} from 'react';
import { useAuth } from "../../contexts/AuthContext";
import Button from '../../components/general/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faX} from '@fortawesome/pro-solid-svg-icons';
import {faCheck} from '@fortawesome/pro-solid-svg-icons';
import {faSpinner} from '@fortawesome/pro-solid-svg-icons';
import {Link} from 'react-router-dom';
import useLogs from '../../hooks/useLogs';

const EmailVerification = (props) => {

    const {emailIsSent=true, hideDashboardLink=false, onSuccess=null} = props;

    const {checkIfVerifiedEmail, currentUser, verifyEmail, refreshEmailVerification} = useAuth();
    const [emailIsVerified, setEmailIsVerified] = useState(null);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [verificationEmailSent, setVerificationEmailSent] = useState(emailIsSent);
    const [notYetClicked, setNotYetClicked] = useState(false);
    const [error, setError] = useState(false);
    const [verificationCheckError, setVerificationCheckError] = useState(false);
    const [emailResentSuccess, setEmailResentSuccess] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const {saveErrorLog} = useLogs();

    useEffect(()=>{
        const check = async () => {
            let results = await checkIfVerifiedEmail();
            setEmailIsVerified(results);
            if (results === true){
                // Refresh the email verification status in context
                await refreshEmailVerification();
                if (onSuccess !== null){
                    onSuccess();
                }
            }
            setInitialLoading(false);
        }
        check();
    },[currentUser, checkIfVerifiedEmail, refreshEmailVerification]);

    const verifyEmailClick = async () => {
        setButtonLoading(true);
        setError(false);
        setEmailResentSuccess(false);
        setVerificationCheckError(false);
        try {
            await verifyEmail(currentUser);
            setVerificationEmailSent(true);
            setEmailResentSuccess(true);
            // Clear success message after 5 seconds
            setTimeout(() => {
                setEmailResentSuccess(false);
            }, 5000);
        } catch (err) {
            console.log(err);
            setError(true);
            saveErrorLog({errorObject: err, functionName: "verifyEmailClick"});
        }
        setButtonLoading(false);
    }

    const reloadEmailVerification = async () => {
        setButtonLoading(true);
        setNotYetClicked(false);
        setError(false);
        setVerificationCheckError(false);
        let results = await checkIfVerifiedEmail();
        setEmailIsVerified(results);
        if (results === true){
            // Refresh the email verification status in context
            await refreshEmailVerification();
            if (onSuccess !== null){
                onSuccess();
            }
            setVerificationCheckError(false);
        } else {
            setVerificationCheckError(true);
            setNotYetClicked(true);
        }
        setButtonLoading(false);
    }
    return (
    <>  
        {initialLoading ? 
            <>
                <div className="flex flex-row justify-center">
                    <FontAwesomeIcon className={buttonLoading ? "fa-pulse " : " "} icon={faSpinner} />
                </div>
            </>
        :
            <div>
                {emailIsVerified === false &&
                    <>
                        <div className="flex flex-col gap-6">
                            {/* Status Message */}
                            <div className="flex flex-row gap-2 items-center justify-center">
                                <div className="text-burnt-sienna">
                                    <FontAwesomeIcon icon={faX} />
                                </div>
                                <div className="text-gray-700 dark:text-gray-300 font-medium">
                                    Your email is not yet verified
                                </div>
                            </div>

                            {/* Instructions */}
                            <div className="text-center space-y-2">
                                {verificationEmailSent ? 
                                    <>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            We&apos;ve sent a verification link to your email inbox. If you&apos;ve already clicked the link in your email, click the button below to confirm.
                                        </p>
                                        {notYetClicked && (
                                            <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                                                Make sure to check your spam folder if you don&apos;t find our email.
                                            </p>
                                        )}
                                    </>
                                :
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Click the button below to send a verification email to your inbox.
                                    </p>
                                }
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                {verificationEmailSent ? 
                                    <>
                                        <Button 
                                            onClick={reloadEmailVerification} 
                                            text={buttonLoading ? <FontAwesomeIcon className="fa-pulse" icon={faSpinner} /> : "I already clicked on the link"} 
                                            color="green" 
                                        />
                                        <Button 
                                            onClick={verifyEmailClick} 
                                            text={buttonLoading ? <FontAwesomeIcon className="fa-pulse" icon={faSpinner} /> : "Resend email"} 
                                            color="charcoal" 
                                        />
                                    </>
                                :
                                    <Button 
                                        onClick={verifyEmailClick} 
                                        text={buttonLoading ? <FontAwesomeIcon className="fa-pulse" icon={faSpinner} /> : "Send verification email"} 
                                        color="green" 
                                    />
                                }
                            </div>

                            {/* Email Resent Success Message */}
                            {emailResentSuccess === true &&
                                <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/30 rounded-lg p-4">
                                    <div className="flex items-center gap-2 justify-center">
                                        <FontAwesomeIcon icon={faCheck} className="text-green-600 dark:text-green-400" />
                                        <p className="text-sm text-green-700 dark:text-green-300 text-center">
                                            <span className="font-semibold">Verification email sent successfully!</span> Please check your inbox and click on the confirmation link.
                                        </p>
                                    </div>
                                </div>
                            }

                            {/* Verification Check Error Message */}
                            {verificationCheckError === true &&
                                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
                                    <p className="text-sm text-red-700 dark:text-red-300 text-center">
                                        <span className="font-semibold">Email not verified yet.</span> Please make sure you clicked the verification link in the email we sent you. If you haven&apos;t received the email, try resending it.
                                    </p>
                                </div>
                            }

                            {/* Send Email Error Message */}
                            {error === true &&
                                <div className="text-center">
                                    <p className="text-sm text-red-600 dark:text-red-400">
                                        Something went wrong sending the email. Please try again or contact Yalango support.
                                    </p>
                                </div>
                            }
                        </div>
                    </>
                }
                {emailIsVerified === true && 
                    <>
                        <div className="flex flex-row gap-2 justify-center">
                            <div className="text-persian-green">
                                <FontAwesomeIcon icon={faCheck} />
                            </div>
                            <div className="text-gray-600">
                                Your email is successfully verified.
                            </div>
                        </div>
                        {hideDashboardLink === false &&
                            <div className="no-underline flex flex-row justify-center">
                                <Link to="/dashboard" className="no-underline">
                                    <Button text="Go to your dashboard" color="green" />
                                </Link>
                            </div>
                        }
                    </>
                }
            </div>
        }
    </>
  )
}

export default EmailVerification